import { useState } from "react";

import { api } from "../lib/http.ts";

function Star({ filled }: { filled: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2.5l2.9 5.9 6.5.95-4.7 4.58 1.1 6.47L12 17.9l-5.8 3.05 1.1-6.47L2.6 9.9l6.5-.95L12 2.5z"
        fill={filled ? "#F5B400" : "none"}
        stroke={filled ? "#F5B400" : "var(--text-3)"}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * 5-star rating + optional comment that posts to /reviews.
 * - default (compact): inline widget for the global footer; the comment field
 *   appears after a rating is picked.
 * - block: a prominent stacked layout (stars + an always-visible, roomy textarea)
 *   for the meeting summary page.
 */
export function FeedbackStars({ block = false }: { block?: boolean } = {}) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit() {
    if (rating < 1 || sending) return;
    setSending(true);
    try {
      await api("/reviews", { method: "POST", body: { rating, text: text.trim() || undefined } });
      setSent(true);
    } catch {
      // Surface minimally; keep the widget usable.
      setSent(true);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return <span className="feedback-thanks">Thanks for your feedback! ★</span>;
  }

  if (block) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, fontSize: 12.5, color: "var(--text-1)" }}>Rate SellSynq:</span>
          <span className="feedback-star-row" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                type="button"
                key={n}
                className="feedback-star-btn"
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                onMouseEnter={() => setHover(n)}
                onFocus={() => setHover(n)}
                onClick={() => setRating(n)}
              >
                <Star filled={(hover || rating) >= n} />
              </button>
            ))}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
          <textarea
            placeholder="Optional comment — what to improve?"
            value={text}
            maxLength={2000}
            rows={2}
            onChange={(e) => setText(e.target.value)}
            style={{
              flex: 1,
              minHeight: 44,
              padding: "7px 10px",
              borderRadius: 8,
              border: "1px solid var(--border)",
              fontSize: 13,
              lineHeight: 1.45,
              resize: "vertical",
              background: "var(--surface)",
              color: "var(--text-1)",
            }}
          />
          <button
            type="button"
            className="pill-btn primary"
            onClick={submit}
            disabled={sending || rating < 1}
            title={rating < 1 ? "Pick a star rating first" : ""}
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <span className="feedback-stars">
      <span className="feedback-label">Rate SellSynq:</span>
      <span className="feedback-star-row" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            type="button"
            key={n}
            className="feedback-star-btn"
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            onMouseEnter={() => setHover(n)}
            onFocus={() => setHover(n)}
            onClick={() => setRating(n)}
          >
            <Star filled={(hover || rating) >= n} />
          </button>
        ))}
      </span>
      {rating > 0 && (
        <>
          <input
            type="text"
            className="feedback-input"
            placeholder="Optional comment…"
            value={text}
            maxLength={500}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
          />
          <button type="button" className="feedback-send" onClick={submit} disabled={sending}>
            {sending ? "Sending…" : "Send"}
          </button>
        </>
      )}
    </span>
  );
}
